/** @jsxImportSource @emotion/react */
import * as s from '../../core/style';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GEOSERVER } from 'config/URL/URL';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style } from 'ol/style';
import Icon from 'ol/style/Icon';
import { getCenter } from 'ol/extent';
import useBaseOlMap from 'hooks/useBaseOlMap';
import useMapOverlay from 'hooks/map/useMapOverlay';
import useFeatureSelection from 'hooks/map/useFeatureSelection';
import useDynamicVectorLayers from 'hooks/map/useDynamicVectorLayers';
import HousingPopup from '../../popups/HousingPopup';
import { fetchHousingInfo } from 'services/lab2/housingService';
import { buildHousingSymbol } from 'utils/lab2/symbolBuilders';
import useViewportParams from 'hooks/map/useViewportParams';
import HousingChartModal from 'components/charts/HousingChartModal';

const createBoundaryLayer = (layerName) =>
    new TileLayer({
        source: new TileWMS({
            url: GEOSERVER + '/geoserver/ows',
            params: {
                LAYERS: layerName,
                TILED: true,
                VERSION: '1.1.1',
                FORMAT: 'image/png',
                TRANSPARENT: true,
            },
            serverType: 'geoserver',
            transition: 0,
        }),
        visible: true,
    });

const HousingMap = ({ pointParams, selectedFilter }) => {
    const { mapRef, initializeMap, isMapReady } = useBaseOlMap({
        targetId: 'housing_map_area',
    });
    const housingOverlayOptions = useMemo(() => ({ autoPan: false }), []);
    const overlayRef = useMapOverlay({
        mapRef,
        elementId: 'housing_popup',
        isMapReady,
        options: housingOverlayOptions,
    });
    const boundaryLayerRef = useRef(null);
    const [filter, setFilter] = useState('housing_count');
    const [selectedLocation, setSelectedLocation] = useState();
    const { syncLayers: syncHousingLayers } = useDynamicVectorLayers({ mapRef });
    const viewportParams = useViewportParams({
        mapRef,
        isMapReady,
        resolveLayerName: (zoom) => (zoom < 11 ? 'sido' : 'sgg'),
    });
    const mapLayerName = viewportParams?.layerName ?? 'sido';
    const currentExtent = viewportParams?.extents;

    const [ housingPopupState, setHousingPopupState ] = useState(false);
    const [ popDetailModalIsOpen, setPopDetailModalIsOpen ] = useState(false);
    
    useEffect(() => {
        initializeMap();
    }, [initializeMap]);

    useEffect(() => {
        if (boundaryLayerRef.current) {
            boundaryLayerRef.current.getSource().updateParams({ LAYERS: mapLayerName });
        }
    }, [mapLayerName]);

    useEffect(() => {
        if (selectedFilter === null || selectedFilter === undefined) {
            setFilter('housing_count');
        } else {
            setFilter(selectedFilter.value);
        }
    }, [selectedFilter]);

    const reloadHousingLayer = useCallback(async () => {
        const map = mapRef.current;
        if (!map || !pointParams) return;

        const extent = currentExtent ?? map.getView().calculateExtent(map.getSize());
        
        const geojsonData = await fetchHousingInfo({
            extent,
            housingParams: pointParams,
            layerName: mapLayerName,
        });

        syncHousingLayers(
            geojsonData?.features?.length
                ? [
                      {
                          id: 'housing-layer',
                          zIndex: 80,
                          createLayer: (mapInstance) => {
                              const features = new GeoJSON().readFeatures(geojsonData, {
                                  featureProjection: mapInstance.getView().getProjection(),
                              });
                              if (!features.length) return null;
                              const source = new VectorSource({ features });
                              const layer = new VectorLayer({
                                  source,
                                  style: (feature) =>
                                      new Style({
                                          image: new Icon({
                                              src:
                                                  'data:image/svg+xml;utf8,' +
                                                  encodeURIComponent(buildHousingSymbol(feature, filter)),
                                              anchor: [0.5, 0.5],
                                              scale: 1,
                                          }),
                                      }),
                              });
                              layer.setZIndex(80);
                              return layer;
                          },
                      },
                  ]
                : []
        );
    }, [currentExtent, filter, mapLayerName, mapRef, pointParams, syncHousingLayers]);

    useEffect(() => {
        if (!isMapReady || !pointParams) {
            return;
        }
        reloadHousingLayer();
    }, [filter, isMapReady, mapLayerName, pointParams, reloadHousingLayer]);

    const handleFeatureSelect = useCallback((selection) => {
        if(selection) {
            closeHousingChartModal(false);
            const extent = selection?.feature?.getGeometry()?.getExtent();
            setSelectedLocation({...selection, coord: getCenter(extent) });
        } else {
            setSelectedLocation(undefined);
        }
    }, []);

    const handleHousingFeatureClick = useFeatureSelection({
        mapRef,
        overlayRef,
        featureToSelection: (feature) => ({
            hjdCd: feature.get('hjd_cd'),
            hjdNm: feature.get('hjd_nm'),
            stock: feature.get('stock'),
            stockRate: feature.get('stock_rate'),
            trd: feature.get('trd'),
            trdRate: feature.get('trd_rate'),
            trdeDeal: feature.get('trde_deal'),
            trdeDealRate: feature.get('trde_deal_rate'),
            deposit: feature.get('deposit'),
            depositRate: feature.get('deposit_rate'),
            feature,
        }),
        onSelectionChange: handleFeatureSelect,
    });

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) return;

        if (!boundaryLayerRef.current) {
            const boundaryLayer = createBoundaryLayer(mapLayerName);
            boundaryLayer.set('name', 'boundaryLayer');
            map.addLayer(boundaryLayer);
            boundaryLayerRef.current = boundaryLayer;
        }

        map.on('click', handleHousingFeatureClick);
        return () => {
            map.un('click', handleHousingFeatureClick);
        };
    }, [handleHousingFeatureClick, isMapReady, mapLayerName, mapRef]);
    
    useEffect(() => {
        if(!selectedLocation) {
            overlayRef.current?.setPosition(undefined);
        } else {
            overlayRef.current?.setPosition(selectedLocation.coord);
        }
    }, [selectedLocation])

    useEffect(() => {
        setSelectedLocation(undefined);
        setPopDetailModalIsOpen(false)
    }, [mapLayerName, selectedFilter, pointParams])

    const handleClickPopupClose = () => {
        setSelectedLocation(undefined);
    }

    const openHousingChartModal = () => {
        setPopDetailModalIsOpen(true);
        overlayRef.current?.setPosition(undefined);
    }

    const closeHousingChartModal = () => {
        setPopDetailModalIsOpen(false);
    }

    return (
        <div css={s.mainContainer}>
            <div id="housing_map_area" css={s.mapArea}></div>

            {/* 주택정보 Popup*/}
            <HousingPopup
                selectedLocation={selectedLocation}
                housingPopupState={housingPopupState}
                setHousingPopupState={setHousingPopupState}
                handlePopupClose={handleClickPopupClose}
                onClickPopDetailButton={openHousingChartModal}
            />

            {/* 주택정보 상세조회 Popup */}
            <HousingChartModal 
                isOpen={popDetailModalIsOpen}
                onClose={closeHousingChartModal}
                location={selectedLocation}
                layerName={mapLayerName}
                pointParams={pointParams}
                selectedLocation={selectedLocation}
            />
        </div>
    );
};

export default HousingMap;
